import { useRef } from 'react';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';

/**
 * Modale de confirmation LOURDE de la SUPPRESSION des COPIES EXACTES (LOT 2/2).
 *
 * Calquée sur `DedupConfirmModal` (T6b) : vraie boîte de dialogue courte
 * (`role="dialog"` + `aria-modal`, focus initial/restauré, verrou de défilement,
 * Échap ferme). Le caller l'enveloppe dans un `<Portal>` (l'en-tête porte
 * `backdrop-blur`).
 *
 * Irréversible : explique que N copies redondantes seront SUPPRIMÉES du disque et
 * qu'un exemplaire est conservé par image.
 */
interface ExactConfirmModalProps {
  /** Nombre de copies redondantes qui seront supprimées (chemins non gardés). */
  count: number;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ExactConfirmModal({
  count,
  busy,
  onConfirm,
  onClose,
}: ExactConfirmModalProps): React.JSX.Element {
  const { t, tp } = useI18n();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y({ dialogRef, initialFocusRef: confirmRef, onEscape: !busy ? onClose : undefined });

  // Verrou de défilement + focus initial (bouton de confirmation) + restauration.

  // Échap ferme (sauf pendant l'appel réseau : on n'interrompt pas une action en cours).

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('dedup.exact.confirmTitle')}
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        {/* En-tête. */}
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <ExclamationTriangleIcon className="h-6 w-6 text-danger" aria-hidden="true" />
            {t('dedup.exact.confirmTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            title={t('dedup.exact.confirmClose')}
            aria-label={t('dedup.exact.confirmClose')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Corps. */}
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4">
          <p className="flex items-start gap-2 rounded-md border border-danger/40 bg-surface-2 p-3 text-sm text-fg">
            <ExclamationTriangleIcon
              className="mt-0.5 h-5 w-5 shrink-0 text-danger"
              aria-hidden="true"
            />
            <span>
              <span className="font-medium">{t('dedup.exact.confirmWarning')}</span>
              <span className="mt-1 block text-muted">
                {interpolate(t('dedup.exact.confirmBody'), { count })}
              </span>
            </span>
          </p>
        </div>

        {/* Actions. */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            {t('dedup.exact.confirmCancel')}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md bg-danger px-4 py-2 text-sm font-medium text-danger-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? (
              <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <TrashIcon className="h-5 w-5" aria-hidden="true" />
            )}
            {busy ? t('dedup.exact.applying') : tp('dedup.exact.confirmApply', count)}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
